import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder, NG_VALUE_ACCESSOR, NG_VALIDATORS, UntypedFormControl, ControlValueAccessor} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AppointmentType, ICustomer, IResponse } from '@signal/asp-data-commons';
import { SubscriptionList } from 'src/app/modules/shared/models/asp.types';
import { DirtyErrorStateMatcher, unsubscribeSubscriptions } from 'src/app/modules/shared/services/util.service';
import { SearchCustomersService } from './../../../../../../src/app/modules/shared/services/search-customers.service';
import { distinctUntilChanged, switchMap, debounceTime } from 'rxjs/operators';
import { toData } from 'src/app/modules/store/app.adapter';
import { MatDialog } from '@angular/material/dialog';
import { CustomerDialogComponent } from '../customer-dialog/customer-dialog.component';
import { formatPhoneNumber } from './../../../../../../src/app/modules/store/search-customers/search-customers.adapter';
import { emailValidator, phoneValidator } from 'src/app/modules/shared/validators/custom-email-validator';
import { CustomerType } from '../../../shared/enums/customer-types.enum';

@Component({
  selector: 'app-contact-email-info',
  templateUrl: './contact-email-info.component.html',
  styleUrls: ['./contact-email-info.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContactEmailInfoComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ContactEmailInfoComponent),
      multi: true
    }
  ]
})
export class ContactEmailInfoComponent implements ControlValueAccessor, OnInit, OnDestroy {

  contactEmailInfo: UntypedFormGroup;
  preferredCountries: string = this.translate.currentLang ? this.translate.currentLang.split('-')[1].toLowerCase(): 'us';
  subscriptions: SubscriptionList = {};
  @Output() formEdited:EventEmitter<boolean> = new EventEmitter();
  @Output() isCIformValid:EventEmitter<boolean> = new EventEmitter();
  @Input() apptType:string;
  @Input() customerSearch = true;

  required:boolean;
  selectedCustomer: ICustomer[];
  noCustomerFound = false;
  searchLoading = false;
  matcher = new DirtyErrorStateMatcher();

  constructor( private readonly formBuilder: UntypedFormBuilder,
    private readonly translate: TranslateService,
    private searchCustomersService: SearchCustomersService,
    public dialog: MatDialog,private readonly cdref: ChangeDetectorRef   ) { 
    this.initializeForm();

  }

  ngOnInit(): void {
    this.subscriptions.formChangeSub1 = this.contactEmailInfo.valueChanges.pipe(
    debounceTime(100),
    distinctUntilChanged()).subscribe(value => {
        this.onCustomerTypeChange(value.customerType, false);
        this.onChange(value);
        this.onTouched();
        this.formEdited.emit(!this.contactEmailInfo.dirty);
        this.isCIformValid.emit(!this.disableSearchBtn() && this.contactEmailInfo.valid ? true : false);
    });
    if (this.apptType === AppointmentType.TEST_DRIVE || this.apptType === AppointmentType.VEHICLE_INQUIRY || this.apptType==="DIGI_CHK") {
      this.required = false
    } else {
      this.required = true;
    }
    /* remove phone and email validations for test drive,vehicle inquiry appt and digital checklist */
    if(!this.required)
    {
      this.contactEmailInfo.controls['phoneNumber'].setValidators([phoneValidator]);
      this.contactEmailInfo.controls['phoneNumber'].updateValueAndValidity();
      this.contactEmailInfo.controls['emailAddress'].setValidators([emailValidator]);
      this.contactEmailInfo.controls['emailAddress'].updateValueAndValidity();
    }
    this.isCIformValid.emit(!this.disableSearchBtn() && this.contactEmailInfo.valid ? true : false);
    this.cdref.detectChanges();
  }

  initializeForm() {
    this.contactEmailInfo = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: [''],
      phoneNumber: ['', [Validators.required, phoneValidator]],
      emailAddress: ['', [Validators.required, emailValidator]],
      drvaFlag: [false],
      customerType: [''],
      businessName: ['']
    });
  }

  disableSearchBtn() {
    if (this.apptType === 'DIGI_CHK') {
      return false;
    }
    if(this.searchLoading) {
      return this.searchLoading;
    }
    const email = this.contactEmailInfo.get('emailAddress').value; 
    const phoneNo = this.contactEmailInfo.get('phoneNumber').value;
    if (email || phoneNo) {
      return null;
    } else {
      return true;
    }
  }
  
  searchCustomer() {
    this.noCustomerFound = false;
    const query = this.getQuery();

    if (query) {
      this.searchLoading = true;
      this.searchCustomersService.getCustomers('', '', query).pipe(
        switchMap((customers: IResponse<ICustomer[]>) => toData(customers))).subscribe(results => this.handleResults(results),
        error => {
          this.searchLoading = false;
          console.log(error);
        });
    }
  }

  getQuery(): string {
      const email = this.contactEmailInfo.get('emailAddress').valid;
      const phoneNo = this.contactEmailInfo.get('phoneNumber').valid;

      if (email && this.contactEmailInfo.get('emailAddress').value) {
          return this.contactEmailInfo.get('emailAddress').value;
      } else if (phoneNo && this.contactEmailInfo.get('phoneNumber').value) {
          return this.contactEmailInfo.get('phoneNumber').value;
      }
      return '';
  }

  handleResults(results: ICustomer[]) { 
      if (results.length === 1) {
          this.handleSingleResult(results);
      } else if (results.length > 1) { 
          this.handleMultipleResults(results);
      } else {
          this.noCustomerFound = true;
      }
      this.searchLoading = false;
  }

  handleSingleResult(results: ICustomer[]) {
      if(results[0].drvaFlag){
        results[0].phoneNumber = this.contactEmailInfo.get('phoneNumber').value;
        results[0].emailAddress = this.contactEmailInfo.get('emailAddress').value;
      }
        this.selectedCustomer = formatPhoneNumber(results);
      if (this.selectedCustomer.length) {
          this.contactEmailInfo.patchValue(this.selectedCustomer[0]);
      }
  }

  handleMultipleResults(results: ICustomer[]) {
      const dialogRef = this.dialog.open(CustomerDialogComponent, {
          width: '600px',
          height: '400px',
          data: results
      });

      dialogRef.afterClosed().subscribe(selectedCustomer => {
        if(selectedCustomer) {
          if(selectedCustomer.drvaFlag){
            selectedCustomer.phoneNumber = this.contactEmailInfo.get('phoneNumber').value;
            selectedCustomer.emailAddress = this.contactEmailInfo.get('emailAddress').value;
          }
            this.selectedCustomer = formatPhoneNumber([selectedCustomer]);
          if (this.selectedCustomer.length) {
              this.contactEmailInfo.patchValue(this.selectedCustomer[0]);
          }
        }
      });
  }

  onCustomerTypeChange(isChecked: boolean | CustomerType, emitEvent = true): void {
    const customerTypeControl = this.contactEmailInfo.controls?.customerType;
    const businessNameControl = this.contactEmailInfo.controls?.businessName;
    const firstNameControl = this.contactEmailInfo.controls.firstName;
    const lastNameControl = this.contactEmailInfo.controls.lastName;
    const isBusiness = isChecked === true || isChecked === CustomerType.BUSINESS; 

    if (isBusiness) {
      customerTypeControl?.patchValue(CustomerType.BUSINESS, { emitEvent });
      businessNameControl?.setValidators([Validators.required]);
      businessNameControl?.setErrors(null);
      firstNameControl.markAsUntouched();
      firstNameControl.clearValidators();
      firstNameControl.setErrors(null);
      lastNameControl.markAsUntouched();
      lastNameControl.clearValidators();
      lastNameControl.setErrors(null);
    } else {
      customerTypeControl?.patchValue(CustomerType.INDIVIDUAL, { emitEvent });
      businessNameControl?.setErrors(null);
      businessNameControl?.clearValidators();
      firstNameControl.setValidators(Validators.required);
      firstNameControl.setErrors(null);
      lastNameControl.setValidators(Validators.required);
      lastNameControl.setErrors(null);
      this.contactEmailInfo.patchValue({ businessName: '' }, { emitEvent });
    }
    customerTypeControl.updateValueAndValidity({ emitEvent });
    businessNameControl.updateValueAndValidity({ emitEvent });
    firstNameControl.updateValueAndValidity({ emitEvent });
    lastNameControl.updateValueAndValidity({ emitEvent });
    this.isCIformValid.emit(!this.disableSearchBtn() && this.contactEmailInfo.valid ? true : false);
  }

  isControlRequired(controlName: string): boolean {
    const control = this.contactEmailInfo.get(controlName);
    if (!control?.validator) {
      return false;
    }
    const validator = control.validator({} as any);
    return !!validator?.['required'];
  }

  onChange = (value: ICustomer) => { };
  onTouched = () => { };

  get value(): ICustomer {
    return this.contactEmailInfo.value;
  }

  set value(value: ICustomer) {
    this.contactEmailInfo.patchValue(value);
    this.onChange(value);
    this.onTouched();
  }

  writeValue(value) {
    if (value) {
      this.value = value;
    }
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  registerOnChange = (fn: any) => {
    this.onChange = fn;
  }

  validate(_: UntypedFormControl) {
    return this.contactEmailInfo.valid ? null : { customer: { valid: false } };
  }

  get f() {
    return this.contactEmailInfo.controls;
  }

  ngOnDestroy() {
    unsubscribeSubscriptions(this.subscriptions);
  }

}
